// AddMappingDialog — Person + Team + Role + optional note picker
// (PRODOPSAI-510 / US7; PRODOPSAI-521 polish iteration;
// PRODOPSAI-523 role-becomes-optional + "Same as my title" Entra binding).
//
// Wired in two places by `AdminTeamMappingPage`:
//   1. The header `[+ Add mapping]` button -> opens with empty `prefill`.
//   2. The Unmapped strip's `[Map to team →]` button -> opens with
//      `prefill={personId}` and the Person picker locked.
//
// Native `<dialog>` lives in `@/components/ui/Dialog` (S6819 — never
// `role="dialog"` on a `<div>`). Pure helpers live in
// `./add-mapping-helpers.ts` so SonarCloud rules S7721 + S3776 cannot fire.
//
// One-fetch-per-open: persons, teams, and roles are fetched the first time
// the dialog opens and re-fetched only when the dialog re-opens (US7 spec
// "fetched once on dialog open"). The page is the lifetime — closing &
// re-opening triggers a fresh fetch, which is what the user expects after
// editing membership data elsewhere.
//
// PRODOPSAI-521: the three native `<select>`s are now `SearchableSelect`s
// (combobox-style with type-ahead). The role picker carries a bottom slot
// `[+ Create new role…]` that swaps the row in-place to a tiny inline form
// (NOT a nested dialog — same `<form>`, same `<dialog>` instance) so the
// user can add a role without losing the mapping context.
//
// PRODOPSAI-523: role becomes OPTIONAL. The picker grows a pinned-top row
// "Same as my title" that binds the membership dynamically to the linked
// Person's Entra `job_title`. The framing sentence above the picker tells
// the admin what title (if any) the selected Person currently has.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  ICreateTeamMembershipRequest,
  IPersonListItem,
  ITeam,
  ITeamRole,
} from '@skystone/shared';
import {
  Button,
  DialogFooter,
  ErrorBanner,
  SearchableSelect,
  Spinner,
  type SearchableOption,
} from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { inputClass } from '@/lib/form-styles';
import { cn, getErrorMessage } from '@/lib/utils';
import { personApi } from '@/services/person-api';
import { teamApi } from '@/services/team-api';
import { teamMembershipApi } from '@/services/team-membership-api';
import {
  buildCreateBody,
  buildPersonOptions,
  buildRoleOptions,
  buildTeamOptions,
  canSaveMapping,
  groupTeamsByRegionLabel,
  isConflictError,
  MAX_NOTE_LENGTH,
  SAME_AS_MY_TITLE_VALUE,
  type MappingPersonOption,
} from './add-mapping-helpers';
import { InlineCreateRoleForm } from './InlineCreateRoleForm';

interface AddMappingDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after a 201 — page refetches the tree. */
  onSaved: (created: { displayName: string; teamName: string; roleName: string }) => void;
  /**
   * When set, preselects + locks the matching picker(s): `personId` locks the
   * Person picker (from the Unmapped strip), `teamId` locks the Team picker
   * (from a team's "Add to team" — PRODOPSAI-539). Either or both may be set.
   */
  prefill?: { personId?: string; teamId?: string } | null;
}

// Pages cap that's well above any plausible org size so the People table
// returns every Person in one shot. Mirrors AdminPeoplePage which uses the
// same value for the same reason (feedback_backend_pagesize_caps_client_pagination).
const PERSON_LIST_PAGE_SIZE = 10000;

interface DialogData {
  persons: IPersonListItem[];
  teams: ITeam[];
  roles: ITeamRole[];
}

function findPersonOption(
  options: readonly MappingPersonOption[],
  id: string | null,
): MappingPersonOption | null {
  if (id === null || id === '') return null;
  return options.find((p) => p.value === id) ?? null;
}

function findPersonListItem(
  items: readonly IPersonListItem[],
  id: string,
): IPersonListItem | null {
  if (id === '') return null;
  return items.find((p) => p.id === id) ?? null;
}

function toSearchablePersonOptions(opts: readonly MappingPersonOption[]): SearchableOption[] {
  return opts.map((p) => ({
    value: p.value,
    label: p.label,
    sublabel: p.email,
  }));
}

/** PRODOPSAI-523: framing sentence shown above the role picker. Pulled out so
 *  the dialog body stays under SonarCloud's S3776 cognitive-complexity gate. */
function PersonFraming({ jobTitle }: Readonly<{ jobTitle: string | null }>) {
  if (jobTitle === null || jobTitle.trim() === '') {
    return (
      <p
        className="mb-2 text-sm text-muted-foreground"
        data-testid="add-mapping-framing-no-title"
      >
        I have no Entra title.
        <br />
        In this team I have the role of:
      </p>
    );
  }
  return (
    <p
      className="mb-2 text-sm text-muted-foreground"
      data-testid="add-mapping-framing-with-title"
    >
      {'I am a '}
      <strong className="text-foreground">{jobTitle}</strong>
      {' in Entra.'}
      <br />
      In this team I have the role of:
    </p>
  );
}

export function AddMappingDialog({
  open,
  onClose,
  onSaved,
  prefill,
}: Readonly<AddMappingDialogProps>) {
  // Form state.
  const [personId, setPersonId] = useState<string>('');
  const [teamId, setTeamId] = useState<string>('');
  const [teamRoleId, setTeamRoleId] = useState<string>('');
  const [useEntraTitle, setUseEntraTitle] = useState<boolean>(false);
  const [note, setNote] = useState<string>('');
  const [personLocked, setPersonLocked] = useState<boolean>(false);
  const [teamLocked, setTeamLocked] = useState<boolean>(false);
  const [creatingRole, setCreatingRole] = useState<boolean>(false);

  // Picker data + lifecycle.
  const [data, setData] = useState<DialogData | null>(null);
  const [dataLoading, setDataLoading] = useState<boolean>(false);
  const [dataError, setDataError] = useState<string | null>(null);

  // Save lifecycle.
  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [conflictError, setConflictError] = useState<string | null>(null);

  // Reset form whenever the dialog closes so re-opening starts fresh.
  useEffect(() => {
    if (open === true) return;
    setPersonId('');
    setTeamId('');
    setTeamRoleId('');
    setUseEntraTitle(false);
    setNote('');
    setPersonLocked(false);
    setTeamLocked(false);
    setSaveError(null);
    setConflictError(null);
    setSaving(false);
    setCreatingRole(false);
  }, [open]);

  // Apply prefill the first time the dialog opens with one.
  useEffect(() => {
    if (open === false) return;
    if (prefill === null || prefill === undefined) return;
    if (prefill.personId !== undefined) {
      setPersonId(prefill.personId);
      setPersonLocked(true);
    }
    if (prefill.teamId !== undefined) {
      setTeamId(prefill.teamId);
      setTeamLocked(true);
    }
  }, [open, prefill]);

  // Fetch the three pickers once when the dialog opens.
  useEffect(() => {
    if (open === false) return;
    let cancelled = false;
    setDataLoading(true);
    setDataError(null);
    Promise.all([
      personApi.list({ pageSize: PERSON_LIST_PAGE_SIZE }),
      teamApi.getAll(),
      teamMembershipApi.getTeamRoles(),
    ])
      .then(([personResp, teams, roles]) => {
        if (cancelled === true) return;
        setData({ persons: personResp.items, teams, roles });
      })
      .catch((err: unknown) => {
        if (cancelled === true) return;
        setDataError(getErrorMessage(err, 'Failed to load mapping pickers'));
      })
      .finally(() => {
        if (cancelled === false) setDataLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const personOptions = useMemo(
    () => (data === null ? [] : buildPersonOptions(data.persons)),
    [data],
  );
  const teamOptions = useMemo(
    () => (data === null ? [] : buildTeamOptions(data.teams)),
    [data],
  );
  const teamGroups = useMemo(() => groupTeamsByRegionLabel(teamOptions), [teamOptions]);
  const roleOptions = useMemo(
    () => (data === null ? [] : buildRoleOptions(data.roles)),
    [data],
  );

  const personSearchOptions = useMemo(
    () => toSearchablePersonOptions(personOptions),
    [personOptions],
  );
  const teamSearchGroups = useMemo(
    () =>
      teamGroups.map((g) => ({
        label: g.region,
        options: g.teams.map((t) => ({ value: t.value, label: t.label })),
      })),
    [teamGroups],
  );

  const selectedPerson = useMemo(
    () => findPersonOption(personOptions, personId),
    [personOptions, personId],
  );
  const selectedPersonListItem = useMemo(
    () => (data === null ? null : findPersonListItem(data.persons, personId)),
    [data, personId],
  );

  const selectedPersonJobTitle = selectedPersonListItem?.jobTitle ?? null;
  const hasEntraTitle = selectedPersonJobTitle !== null && selectedPersonJobTitle.trim() !== '';

  // PRODOPSAI-523: the role picker grows a pinned-top "Same as my title" row
  // when the selected Person has an Entra job_title. We build the unified
  // option list here so the SearchableSelect filtering, keyboard nav, and
  // value-mapping work uniformly across both "real role" and pinned entries.
  const roleSearchOptions = useMemo(() => {
    const realRoles = roleOptions.map((r) => ({ value: r.value, label: r.label }));
    if (hasEntraTitle === false || selectedPersonJobTitle === null) return realRoles;
    return [
      {
        value: SAME_AS_MY_TITLE_VALUE,
        label: `★ Same as my title ("${selectedPersonJobTitle}")`,
      },
      ...realRoles,
    ];
  }, [roleOptions, hasEntraTitle, selectedPersonJobTitle]);

  // PRODOPSAI-523: the role picker's `value` prop. Use the special sentinel
  // when the admin has chosen "Same as my title" so the trigger displays the
  // pinned-row label.
  const rolePickerValue = useEntraTitle === true ? SAME_AS_MY_TITLE_VALUE : teamRoleId;

  const noteLength = note.length;
  const noteOverLimit = noteLength > MAX_NOTE_LENGTH;
  const saveable = canSaveMapping({ personId, teamId, teamRoleId, useEntraTitle, noteLength });

  const selectedTeamLabel = teamOptions.find((t) => t.value === teamId)?.label ?? '';
  const selectedRoleLabel = (() => {
    if (useEntraTitle === true) return selectedPersonJobTitle ?? '(no title)';
    return roleOptions.find((r) => r.value === teamRoleId)?.label ?? '';
  })();

  // Rabizard #663 finding 4: a 409 conflict error message says
  // "This person already has this role on this team". If the user resolves
  // the conflict by changing the person OR the team (not just the role),
  // the stale error must clear too — otherwise contradictory state lingers.
  const clearConflict = useCallback(() => {
    setConflictError(null);
  }, []);

  const handleUnlockPerson = useCallback(() => {
    setPersonLocked(false);
    setPersonId('');
    // Clear the entra-title binding when the person changes — the new person
    // may not have a job_title at all.
    setUseEntraTitle(false);
    clearConflict();
  }, [clearConflict]);

  const handlePersonChange = useCallback(
    (next: string) => {
      setPersonId(next);
      setUseEntraTitle(false);
      clearConflict();
    },
    [clearConflict],
  );

  const handleUnlockTeam = useCallback(() => {
    setTeamLocked(false);
    setTeamId('');
    clearConflict();
  }, [clearConflict]);

  const handleRoleCreated = useCallback(
    (created: ITeamRole) => {
      if (data !== null) {
        // Append the created role to the local cache so the picker shows it.
        const existing = data.roles.some((r) => r.id === created.id);
        if (existing === false) {
          setData({ ...data, roles: [...data.roles, created] });
        }
      }
      setTeamRoleId(created.id);
      setUseEntraTitle(false);
      setCreatingRole(false);
      clearConflict();
    },
    [data, clearConflict],
  );

  // PRODOPSAI-523: bridge the SearchableSelect onChange so picking the pinned
  // "Same as my title" row toggles `useEntraTitle` instead of writing a fake
  // value into `teamRoleId`.
  const handleRolePick = useCallback(
    (value: string) => {
      if (value === SAME_AS_MY_TITLE_VALUE) {
        setUseEntraTitle(true);
        setTeamRoleId('');
      } else {
        setUseEntraTitle(false);
        setTeamRoleId(value);
      }
      clearConflict();
    },
    [clearConflict],
  );

  const handleSave = useCallback(async () => {
    if (saveable === false) return;
    setSaving(true);
    setSaveError(null);
    setConflictError(null);
    const body = buildCreateBody({
      personId,
      teamId,
      teamRoleId,
      useEntraTitle,
      note,
    }) as ICreateTeamMembershipRequest;
    try {
      await teamMembershipApi.createTeamMembership(body);
      // Snapshot display labels before clearing state — onSaved triggers the
      // page-level refetch that may close the dialog before the toast reads.
      const displayName =
        selectedPerson === null ? 'Person' : selectedPerson.label;
      onSaved({
        displayName,
        teamName: selectedTeamLabel,
        roleName: selectedRoleLabel,
      });
      onClose();
    } catch (err: unknown) {
      if (isConflictError(err) === true) {
        setConflictError('This person already has this role on this team');
      } else {
        setSaveError(getErrorMessage(err, 'Failed to create mapping'));
      }
    } finally {
      setSaving(false);
    }
  }, [
    saveable,
    personId,
    teamId,
    teamRoleId,
    useEntraTitle,
    note,
    selectedPerson,
    selectedTeamLabel,
    selectedRoleLabel,
    onSaved,
    onClose,
  ]);

  // Rabizard #665 finding 2: hide [+ Create new role…] while the role list
  // is still loading. The soft-collision check (isLikelyDuplicateRoleName)
  // depends on data.roles; an empty list lets the user create a duplicate
  // that the backend then rejects with 409. Render a loading hint instead.
  const roleFooter = data === null ? (
    <div
      className="px-3 py-2 text-xs text-muted-foreground"
      data-testid="add-mapping-create-role-loading"
    >
      Loading roles…
    </div>
  ) : (
    <button
      type="button"
      onClick={() => setCreatingRole(true)}
      className="w-full rounded-md px-3 py-2 text-left text-sm text-primary hover:bg-muted/50"
      data-testid="add-mapping-create-role-trigger"
    >
      + Create new role…
    </button>
  );

  return (
    <Dialog open={open} onClose={onClose} title="Add team mapping" maxWidth="max-w-xl">
      {dataLoading === true && (
        <div className="flex items-center justify-center py-8" data-testid="add-mapping-loading">
          <Spinner size="lg" />
        </div>
      )}
      {dataError !== null && <ErrorBanner message={dataError} />}
      {dataLoading === false && dataError === null && (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
          data-testid="add-mapping-form"
        >
          <div>
            <span className="mb-1 block text-sm font-medium">
              Person <span className="text-error-500">*</span>
            </span>
            {personLocked === true && selectedPerson !== null ? (
              <div className="flex items-center gap-3" data-testid="add-mapping-person-locked">
                <span className="flex h-10 flex-1 items-center rounded-md border border-input bg-muted/30 px-3 py-2 text-sm">
                  {selectedPerson.label}
                </span>
                <button
                  type="button"
                  onClick={handleUnlockPerson}
                  className="text-xs text-primary hover:underline"
                  data-testid="add-mapping-change-person"
                >
                  Change person
                </button>
              </div>
            ) : (
              <SearchableSelect
                id="add-mapping-person"
                testId="add-mapping-person"
                placeholder="Search Entra-linked persons…"
                options={personSearchOptions}
                value={personId}
                onChange={handlePersonChange}
              />
            )}
          </div>

          <div>
            <span className="mb-1 block text-sm font-medium">
              Team <span className="text-error-500">*</span>
            </span>
            {teamLocked === true && selectedTeamLabel !== '' ? (
              <div className="flex items-center gap-3" data-testid="add-mapping-team-locked">
                <span className="flex h-10 flex-1 items-center rounded-md border border-input bg-muted/30 px-3 py-2 text-sm">
                  {selectedTeamLabel}
                </span>
                <button
                  type="button"
                  onClick={handleUnlockTeam}
                  className="text-xs text-primary hover:underline"
                  data-testid="add-mapping-change-team"
                >
                  Change team
                </button>
              </div>
            ) : (
              <SearchableSelect
                id="add-mapping-team"
                testId="add-mapping-team"
                placeholder="Pick a team…"
                groups={teamSearchGroups}
                value={teamId}
                onChange={(v) => {
                  setTeamId(v);
                  clearConflict();
                }}
              />
            )}
          </div>

          <div>
            <span className="mb-1 block text-sm font-medium">
              Role on team <span className="text-error-500">*</span>
            </span>
            {selectedPerson !== null && (
              <PersonFraming jobTitle={selectedPersonJobTitle} />
            )}
            {creatingRole === true ? (
              <InlineCreateRoleForm
                existingRoles={data?.roles ?? []}
                onCancel={() => setCreatingRole(false)}
                onCreated={handleRoleCreated}
              />
            ) : (
              <SearchableSelect
                id="add-mapping-role"
                testId="add-mapping-role"
                placeholder="Pick a role…"
                options={roleSearchOptions}
                value={rolePickerValue}
                onChange={handleRolePick}
                footer={roleFooter}
              />
            )}
            {conflictError !== null && (
              <p
                className="mt-1 text-xs text-error-600"
                role="alert"
                data-testid="add-mapping-conflict"
              >
                {conflictError}
              </p>
            )}
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <label htmlFor="add-mapping-note" className="text-sm font-medium">
                Note (optional, e.g. {'"PL"'}, {'"part time"'})
              </label>
              <span
                className={cn(
                  'text-xs',
                  noteOverLimit ? 'text-error-600' : 'text-muted-foreground',
                )}
                data-testid="add-mapping-note-counter"
              >
                {`${noteLength} / ${MAX_NOTE_LENGTH}`}
              </span>
            </div>
            <input
              id="add-mapping-note"
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={MAX_NOTE_LENGTH + 50}
              className={inputClass}
              data-testid="add-mapping-note"
            />
            {noteOverLimit && (
              <p className="mt-1 text-xs text-error-600" role="alert">
                Note must be 255 characters or fewer
              </p>
            )}
          </div>

          <p className="text-xs text-muted-foreground" data-testid="add-mapping-help">
            {"Can't find someone? They must be auto-matched from Entra first. → "}
            <Link to="/admin/people" className="text-primary hover:underline">
              Run auto-match
            </Link>
          </p>

          {saveError !== null && <ErrorBanner message={saveError} />}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={saving}
              disabled={saveable === false}
              data-testid="add-mapping-save"
            >
              Save mapping
            </Button>
          </DialogFooter>
        </form>
      )}
    </Dialog>
  );
}
